import { useEffect, useMemo, useState } from 'react';
import { withNamespaces } from 'react-i18next';
import {
    Button,
    Collapse,
} from 'reactstrap';

import { CallAttemptStatuses } from '../../constants';

const WAVEFORM_BARS = 56;

const formatClock = (totalSeconds) => {
    const wholeSeconds = Math.max(0, Math.floor(totalSeconds));
    const minutes = Math.floor(wholeSeconds / 60);
    const seconds = wholeSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const createWaveform = (seed) => {
    let value = seed * 17;

    return Array.from({ length: WAVEFORM_BARS }, (_, index) => {
        value = ((value * 37) + (index * 13)) % 97;

        return 6 + (value % 18);
    });
};

const CallPlayer = ({
    durationSeconds,
    isActive,
    t,
}) => {
    const [elapsed, setElapsed] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const bars = useMemo(() => createWaveform(durationSeconds || 1), [durationSeconds]);
    const progress = durationSeconds ? elapsed / durationSeconds : 0;

    useEffect(() => {
        if (!isPlaying || !isActive) return undefined;

        const timer = setInterval(() => {
            setElapsed((current) => Math.min(durationSeconds, Number((current + 0.1).toFixed(1))));
        }, 100);

        return () => clearInterval(timer);
    }, [durationSeconds, isActive, isPlaying]);

    useEffect(() => {
        if (elapsed >= durationSeconds) setIsPlaying(false);
    }, [durationSeconds, elapsed]);

    useEffect(() => {
        if (!isActive) setIsPlaying(false);
    }, [isActive]);

    return (
        <div className="call-player d-flex align-items-center gap-3">
            <button
                aria-label={t(isPlaying ? 'Pause' : 'Play')}
                className="call-player-toggle"
                onClick={() => {
                    if (elapsed >= durationSeconds) setElapsed(0);

                    setIsPlaying((playing) => !playing);
                }}
                type="button"
            >
                <i className={isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} />
            </button>

            <div className="call-waveform">
                {bars.map((height, index) => (
                    <span
                        key={index}
                        style={{
                            backgroundColor: (index / bars.length) < progress ? '#556ee6' : '#c5cbe3',
                            height,
                        }}
                    />
                ))}
            </div>

            <span className="text-muted font-size-12 text-nowrap">
                {`${formatClock(elapsed)} / ${formatClock(durationSeconds)}`}
            </span>

            <Button color="light" size="sm" type="button">
                <i className="ri-download-2-line align-middle me-1" />
                {t('Download recording')}
            </Button>
        </div>
    );
};

const CallAttempt = ({
    attempt,
    isOpen,
    onToggle,
    phone,
    t,
}) => {
    const status = CallAttemptStatuses[attempt.status];

    return (
        <div className={`call-attempt ${isOpen ? 'is-open' : ''}`}>
            <button
                aria-expanded={isOpen}
                className="call-attempt-toggle"
                onClick={onToggle}
                type="button"
            >
                <span className="call-attempt-marker" />
                <span className="fw-medium">{t(attempt.label)}</span>
                <span className="text-muted">{attempt.at}</span>
                <span className={`badge rounded-pill p-2 ms-auto ${status.className}`}>{t(status.title)}</span>
                <span className="text-muted">{attempt.durationLabel}</span>
                <span className="text-muted text-nowrap">
                    <i className="ri-phone-line align-middle me-1" />
                    {phone}
                </span>
                <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line`} />
            </button>

            <Collapse isOpen={isOpen}>
                <div className="call-attempt-body">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                        <h5 className="mb-0 fw-medium">{`${t('Call attempt')} ${attempt.id}`}</h5>
                        <span className={`badge rounded-pill p-2 ${status.className}`}>{t(status.title)}</span>
                        <span className="text-muted ms-auto">
                            {attempt.at}
                        </span>
                        <span className="text-muted">
                            {`${t('Duration')}: ${attempt.durationLabel}`}
                        </span>
                    </div>

                    <CallPlayer
                        durationSeconds={attempt.durationSeconds}
                        isActive={isOpen}
                        t={t}
                    />

                    <h6 className="mt-4 mb-3">
                        {t('Call transcript')}
                        <span className="text-muted fw-normal ms-1">{`(${t('AI generated')})`}</span>
                    </h6>

                    <ul className="list-unstyled mb-4">
                        {attempt.transcript.map((line) => (
                            <li className="d-flex gap-3 mb-2" key={line.at}>
                                <span className="text-muted font-size-12">{line.at}</span>
                                <span>{line.text}</span>
                            </li>
                        ))}
                    </ul>

                    <div className="call-analysis d-flex align-items-start p-3">
                        <i className="ri-sparkling-line text-primary font-size-18 me-2" />
                        <div>
                            <h6 className="mb-1">{t('AI analysis')}</h6>
                            <p className="mb-0 text-muted">{attempt.analysis}</p>
                        </div>
                    </div>
                </div>
            </Collapse>
        </div>
    );
};

const CallAttempts = ({
    attempts,
    phone,
    t,
}) => {
    const [openAttemptId, setOpenAttemptId] = useState(attempts[attempts.length - 1]?.id);

    return (
        <div>
            <h5 className="fw-bold mb-3">
                {t('Call verification attempts')}
                <span className="badge badge-soft-warning rounded-pill ms-2">{attempts.length}</span>
            </h5>

            {attempts.map((attempt) => (
                <CallAttempt
                    attempt={attempt}
                    isOpen={openAttemptId === attempt.id}
                    key={attempt.id}
                    onToggle={() => setOpenAttemptId((current) => (
                        current === attempt.id ? null : attempt.id
                    ))}
                    phone={phone}
                    t={t}
                />
            ))}
        </div>
    );
};

export default withNamespaces()(CallAttempts);
